import { CircleX } from "lucide-react";
import type { ReactElement } from "react";
import {
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { ActiveFilterButton } from "../misc/ActiveFilterButton";
import {
  BUSINESS_UNITS,
  CONTACT_TYPES,
  type Company,
  type LeadSource,
} from "../types";
import { CONTACT_TYPE_LABEL_KEYS } from "./contactTypes";

/**
 * Almikia Epic EC2a: the list filters of the one address book. Each block renders the same
 * buttons for the filter panel (`ToggleFilterButton`, always shown) and for the mobile summary
 * (`ActiveFilterButton`, shown only while active), selected by the `summary` prop.
 *
 * Filter keys follow the TASK-119 contract: `contact_types@cs`, `contact_types@eq` ('{}' = no
 * type), `company_id@eq`, `lead_source_id@eq`, `business_unit@eq`, `name_needs_review@eq`.
 */

const BUTTON_CLASS = "w-auto md:w-full justify-between h-11 md:h-8";

/** Filter key of the type multi-select (`contains all` of the chosen types). */
export const CONTACT_TYPES_FILTER_KEY = "contact_types@cs";
/** Filter key of the 'Sin tipo' choice (an empty `contact_types`). */
export const NO_TYPE_FILTER_KEY = "contact_types@eq";
export const NO_TYPE_FILTER_VALUE = "{}";

type FilterBlockProps = {
  /** True renders the mobile summary variant (active filters only). */
  summary?: boolean;
  size?: "default" | "sm" | "lg" | "icon" | null;
};

const FilterButton = ({
  summary,
  ...props
}: FilterBlockProps & {
  label: ReactElement | string;
  value: Record<string, unknown>;
}) =>
  summary ? (
    <ActiveFilterButton className={BUTTON_CLASS} {...props} />
  ) : (
    <ToggleFilterButton className={BUTTON_CLASS} {...props} />
  );

/** Parses a `{a,b}` array literal filter value into its items. */
export const parseTypesFilter = (value: unknown): string[] =>
  typeof value === "string"
    ? value
        .replace(/^\{|\}$/g, "")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];

/**
 * Type multi-select: every chosen type narrows the list (the contact must have all of them),
 * plus 'Sin tipo' for legacy contacts with an empty `contact_types` (exclusive with the types).
 */
export const ContactTypeFilterButtons = ({
  summary,
  size,
}: FilterBlockProps) => {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const selected = parseTypesFilter(filterValues[CONTACT_TYPES_FILTER_KEY]);
  const noType = filterValues[NO_TYPE_FILTER_KEY] === NO_TYPE_FILTER_VALUE;

  const apply = (types: string[], withNoType: boolean) => {
    const others = Object.fromEntries(
      Object.entries(filterValues).filter(
        ([key]) =>
          key !== CONTACT_TYPES_FILTER_KEY && key !== NO_TYPE_FILTER_KEY,
      ),
    );
    setFilters({
      ...others,
      ...(types.length > 0
        ? { [CONTACT_TYPES_FILTER_KEY]: `{${types.join(",")}}` }
        : {}),
      ...(withNoType ? { [NO_TYPE_FILTER_KEY]: NO_TYPE_FILTER_VALUE } : {}),
    });
  };

  const typeButton = (
    key: string,
    label: string,
    isSelected: boolean,
    onClick: () => void,
  ) =>
    summary && !isSelected ? null : (
      <Button
        key={key}
        variant={isSelected ? "default" : "ghost"}
        size={size ?? "sm"}
        aria-pressed={isSelected}
        onClick={onClick}
        className={cn(
          "cursor-pointer flex flex-row items-center justify-between gap-2 px-2.5 w-full",
          BUTTON_CLASS,
        )}
      >
        {label}
        {isSelected && <CircleX className="opacity-50" />}
      </Button>
    );

  return (
    <>
      {CONTACT_TYPES.map((type) =>
        typeButton(
          type,
          translate(CONTACT_TYPE_LABEL_KEYS[type], { _: type }),
          selected.includes(type),
          () =>
            apply(
              selected.includes(type)
                ? selected.filter((item) => item !== type)
                : [...selected, type],
              false,
            ),
        ),
      )}
      {typeButton(
        "no-type",
        translate("resources.contacts.filters.no_type"),
        noType,
        () => apply([], !noType),
      )}
    </>
  );
};

/** Origin (`lead_source_id`) buttons, one per catalogue source (the catalogue is small). */
export const OriginFilterButtons = ({ summary, size }: FilterBlockProps) => {
  const { data } = useGetList<LeadSource>("lead_sources", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  return (
    <>
      {(data ?? []).map((source) => (
        <FilterButton
          key={source.id}
          summary={summary}
          size={size}
          label={source.name}
          value={{ "lead_source_id@eq": source.id }}
        />
      ))}
    </>
  );
};

/** Business unit (`business_unit`) buttons: Corretaje and BPO. */
export const BusinessUnitFilterButtons = ({
  summary,
  size,
}: FilterBlockProps) => (
  <>
    {BUSINESS_UNITS.map((unit) => (
      <FilterButton
        key={unit}
        summary={summary}
        size={size}
        label={`resources.contacts.business_units.${unit}`}
        value={{ "business_unit@eq": unit }}
      />
    ))}
  </>
);

/** Company (`company_id`) buttons for the first companies by name. */
export const CompanyFilterButtons = ({ summary, size }: FilterBlockProps) => {
  const { data } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "name", order: "ASC" },
  });
  return (
    <>
      {(data ?? []).map((company) => (
        <FilterButton
          key={company.id}
          summary={summary}
          size={size}
          label={company.name}
          value={{ "company_id@eq": company.id }}
        />
      ))}
    </>
  );
};

/** Roles that review the uncertain name splits. */
const NAME_REVIEW_ROLES = ["direccion", "lead_manager"];

/**
 * 'Revisar nombre' (`name_needs_review`) filter with a count badge, offered to direccion and
 * lead_manager only (the people who fix the names).
 */
export const NameReviewFilterButton = ({ summary, size }: FilterBlockProps) => {
  const { identity } = useGetIdentity();
  const canReview = NAME_REVIEW_ROLES.includes(String(identity?.role));
  const { total } = useGetList(
    "contacts",
    {
      filter: { "name_needs_review@eq": true },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: canReview && !summary },
  );
  const translate = useTranslate();
  if (!canReview) return null;
  return (
    <FilterButton
      summary={summary}
      size={size}
      label={
        <span className="flex items-center gap-2">
          {translate("resources.contacts.name_review.badge")}
          {!summary && total != null ? (
            <Badge variant="secondary" data-testid="name-review-count">
              {total}
            </Badge>
          ) : null}
        </span>
      }
      value={{ "name_needs_review@eq": true }}
    />
  );
};
